import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  Alert,
  Fab,
  Paper,
  Chip,
  Divider,
  LinearProgress,
  Modal,
  Backdrop,
  Fade,
  Slider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CameraAlt as CameraIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { calorieAIService } from '../../services/calorieAI/calorieAIService';
import { aiService } from '../../services/calorieAI/aiService';

const FoodIdentification = ({ onBack, onFoodAdded }) => {
  const [step, setStep] = useState('capture'); // capture, analyze, confirm
  const [capturedImage, setCapturedImage] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState({}); // 改為物件，支援多個食物的編輯狀態
  const [editedFoods, setEditedFoods] = useState([]); // 改為陣列，支援多個食物
  const [imageModalOpen, setImageModalOpen] = useState(false); // 圖片彈出視窗狀態
  const [selectedMealType, setSelectedMealType] = useState('breakfast'); // 全局餐型選擇狀態
  const [globalCompletionPercentage, setGlobalCompletionPercentage] = useState(80); // 全局食用比例
  const [selectedModel, setSelectedModel] = useState('google/gemini-2.0-flash-exp:free'); // 選擇的AI模型
  
  // 新增的狀態管理
  const [saveProgress, setSaveProgress] = useState(0); // 保存進度
  const [currentSavingFood, setCurrentSavingFood] = useState(''); // 當前正在保存的食物
  const [saveResults, setSaveResults] = useState({ success: [], failed: [] }); // 保存結果
  const [isSaving, setIsSaving] = useState(false); // 是否正在保存中
  
  const fileInputRef = useRef(null);

  const mealTypes = [
    { value: 'breakfast', label: '早餐 🌅' },
    { value: 'lunch', label: '午餐 🌞' },
    { value: 'snack', label: '零食 🍪' },
    { value: 'dinner', label: '晚餐 🌙' },
    { value: 'latenight', label: '宵夜 🌃' },
    { value: 'other', label: '其他 🍽️' }
  ];

  // 使用 Capacitor 相機拍照
  const takePicture = async () => {
    try {
      setLoading(true);
      setError(null);

      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
      });

      setCapturedImage(image.dataUrl);
      setStep('analyze');
      await analyzeFood(image.dataUrl);
    } catch (error) {
      console.error('拍照失敗:', error);
      setError('拍照失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  // 從相簿選擇照片
  const selectFromGallery = async () => {
    try {
      setLoading(true);
      setError(null);

      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
      });

      setCapturedImage(image.dataUrl);
      setStep('analyze');
      await analyzeFood(image.dataUrl);
    } catch (error) {
      console.error('選擇照片失敗:', error);
      setError('選擇照片失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  // 使用檔案輸入作為備用方案
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        setCapturedImage(e.target.result);
        setStep('analyze');
        await analyzeFood(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // AI 食物識別分析
  const analyzeFood = async (imageData) => {
    try {
      setLoading(true);
      setError(null);

      // 將 base64 圖片轉換為 File 對象
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], 'food-image.jpg', { type: 'image/jpeg' });

      // 設定使用選擇的模型
      const modelIndex = aiService.getAvailableModels().findIndex(model => model === selectedModel);
      if (modelIndex !== -1) {
        aiService.setModel(modelIndex);
      }

      // 使用真實的 AI 服務進行食物識別
      const result = await aiService.identifyFood(file);
      
      console.log('AI 識別結果:', result);

      // 檢查是否有識別到任何食物
      if (!result.foods || result.foods.length === 0) {
        // 沒有識別到任何食物的情況
        setAnalysisResult(null);
        setEditedFoods([]);
        setEditMode({});
        setError('no_food_detected'); // 設定特殊錯誤狀態
        setStep('capture'); // 回到拍照步驟
        return;
      }

      // 轉換 AI 回應格式以符合現有組件結構，保留所有分析資訊
      const formattedResult = {
        foods: result.foods.map(food => ({
          name: food.name,
          calories: food.calories,
          protein: food.protein || 0,
          carbs: food.carbs || 0,
          fat: food.fat || 0,
          quantity: food.quantity || 1,
          unit: food.unit || '份',
          confidence: food.confidence || 0.5,
          healthyIndex: food.healthyIndex || 3,
          gi_level: food.gi_level || '',
          healthier_alternative: food.healthier_alternative || '',
          food_analysis: food.food_analysis || ''
        })),
        confidence: result.foods[0]?.confidence || 0.5,
        notes: result.notes,
        health_tips: result.health_tips,
        overall_evaluation: result.overall_evaluation,
        // 保留所有詳細分析資訊
        glycemic_analysis: result.glycemic_analysis,
        digestion_analysis: result.digestion_analysis,
        personalized_advice: result.personalized_advice,
        eating_order_suggestions: result.eating_order_suggestions,
        overall_analysis: result.overall_analysis,
        total_calories: result.total_calories
      };

      setAnalysisResult(formattedResult);
      // 初始化多個食物的編輯狀態
      const initialEditedFoods = formattedResult.foods.map(food => ({
        ...food,
        meal_type: selectedMealType,
        completionPercentage: globalCompletionPercentage // 使用全局完成度
      }));
      setEditedFoods(initialEditedFoods);
      
      // 初始化編輯模式狀態
      const initialEditMode = {};
      formattedResult.foods.forEach((_, index) => {
        initialEditMode[index] = false;
      });
      setEditMode(initialEditMode);
      
      setStep('confirm');
    } catch (error) {
      console.error('AI 分析失敗:', error);
      
      // 提供更友好的錯誤訊息
      let errorMessage = 'AI 分析失敗，請重試或手動輸入';
      let shouldShowFallback = true;
      
      if (error.message.includes('API 請求失敗')) {
        errorMessage = 'AI 服務暫時無法使用，請稍後再試';
        shouldShowFallback = false;
      } else if (error.message.includes('解析 AI 回應失敗')) {
        errorMessage = 'AI 無法識別此圖片中的食物，請嘗試重新拍攝或手動輸入';
      } else if (error.message.includes('網路')) {
        errorMessage = '網路連線問題，請檢查網路後重試';
        shouldShowFallback = false;
      } else if (error.message.includes('識別信心度過低')) {
        errorMessage = '圖片中的食物識別信心度較低，建議重新拍攝更清晰的照片，或手動輸入食物資訊';
      } else if (error.message.includes('未能識別到任何食物')) {
        errorMessage = '未能識別到食物，請確認圖片中包含食物並重新拍攝，或選擇手動輸入';
      }
      
      setError(errorMessage);
      
      // 只在特定情況下提供備用的手動輸入選項
      if (shouldShowFallback) {
        const fallbackResult = {
          foods: [{
            name: '未知食物',
            calories: 100,
            protein: 0,
            carbs: 0,
            fat: 0,
            quantity: 1,
            unit: '份',
            confidence: 0.1
          }],
          confidence: 0.1,
          notes: '請手動編輯食物資訊'
        };
        
        setAnalysisResult(fallbackResult);
        // 初始化備用食物的編輯狀態
        const initialEditedFoods = fallbackResult.foods.map(food => ({
          ...food,
          meal_type: selectedMealType,
          completionPercentage: globalCompletionPercentage // 使用全局完成度
        }));
        setEditedFoods(initialEditedFoods);
        
        // 自動進入編輯模式
        setEditMode({ 0: true });
        setStep('confirm');
      } else {
        // 對於網路或 API 錯誤，回到拍照步驟
        setStep('capture');
      }
    } finally {
      setLoading(false);
    }
  };

  // 根據當前時間推測餐型
  const getCurrentMealType = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 11) return 'breakfast';
    if (hour >= 11 && hour < 15) return 'lunch';
    if (hour >= 17 && hour < 22) return 'dinner';
    if (hour >= 22 || hour < 3) return 'latenight';
    return 'snack';
  };

  // 組件初始化時自動設定餐型
  useEffect(() => {
    const autoMealType = getCurrentMealType();
    setSelectedMealType(autoMealType);
  }, []);

  // 生成專業營養師分析
  const generateNutritionalAnalysis = (foods) => {
    if (!foods || foods.length === 0) return null;

    const analysis = [];

    // 描述識別到的食物
    const foodNames = foods.map(food => food.name).join('、');
    if (foods.length === 1) {
      analysis.push(`🔍 AI 識別結果：檢測到「${foodNames}」，讓我為您進行專業營養分析。`);
    } else {
      analysis.push(`🔍 AI 識別結果：檢測到 ${foods.length} 種食物：${foodNames}，以下是綜合營養分析。`);
    }

    // 使用 AI 提供的單一食物分析，如果沒有才用預設的
    foods.forEach((food) => {
      if (food.food_analysis) {
        analysis.push(`🍽️ ${food.name}：${food.food_analysis}`);
      } else {
        // Fallback for older data or if AI didn't provide analysis
        analysis.push(`🍽️ ${food.name}：為身體提供所需的營養素。`);
      }
    });

    // 使用 AI 提供的整體評價
    if (analysisResult?.overall_evaluation) {
      analysis.push(`✨ 綜合評價：${analysisResult.overall_evaluation}`);
    }

    return (
      <Box>
        {analysis.map((item, index) => (
          <Typography key={index} variant="body2" sx={{ mb: 1, lineHeight: 1.6 }}>
            {item}
          </Typography>
        ))}
      </Box>
    );
  };

  // 渲染血糖影響分析
  const renderGlycemicAnalysis = () => {
    if (!analysisResult?.glycemic_analysis) return null;

    const { glycemic_analysis } = analysisResult;
    
    return (
      <Card sx={{ mb: 3, border: '1px solid #ff9800', borderRadius: '12px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#ff9800', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            🩸 血糖影響預估
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 1 }}>
                  升糖指數估算
                </Typography>
                <Typography variant="body2" sx={{ color: '#e65100' }}>
                  {glycemic_analysis.glycemic_index || '中等 (GI: 55-70)'}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#fff3e0', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#f57c00', mb: 1 }}>
                  血糖影響程度
                </Typography>
                <Typography variant="body2" sx={{ color: '#e65100' }}>
                  {glycemic_analysis.blood_sugar_impact || '溫和上升，建議搭配蛋白質食用'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // 渲染消化時間分析
  const renderDigestionAnalysis = () => {
    if (!analysisResult?.digestion_analysis) return null;

    const { digestion_analysis } = analysisResult;
    
    return (
      <Card sx={{ mb: 3, border: '1px solid #4caf50', borderRadius: '12px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#4caf50', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            ⏰ 消化時間預估
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f1f8e9', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#388e3c', mb: 1 }}>
                  預計消化時間
                </Typography>
                <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                  {digestion_analysis.digestion_time || '2-3 小時'}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Box sx={{ p: 2, backgroundColor: '#f1f8e9', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#388e3c', mb: 1 }}>
                  飽腹感持續時間
                </Typography>
                <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                  {digestion_analysis.satiety_duration || '3-4 小時'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // 渲染個人化建議
  const renderPersonalizedAdvice = () => {
    if (!analysisResult?.personalized_advice) return null;

    const { personalized_advice } = analysisResult;
    
    return (
      <Card sx={{ mb: 3, border: '1px solid #9c27b0', borderRadius: '12px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#9c27b0', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            🎯 個人化建議
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f3e5f5', borderRadius: '8px', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#7b1fa2', mb: 1 }}>
                  與目標的匹配度
                </Typography>
                <Typography variant="body2" sx={{ color: '#6a1b9a' }}>
                  {personalized_advice.goal_compatibility || '適合維持目標，建議控制份量'}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ p: 2, backgroundColor: '#f3e5f5', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#7b1fa2', mb: 1 }}>
                  對日常卡路里目標的影響
                </Typography>
                <Typography variant="body2" sx={{ color: '#6a1b9a' }}>
                  {personalized_advice.calorie_impact || '約佔每日目標的 15-20%，建議搭配運動'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  // 渲染進食順序建議
  const renderEatingOrderSuggestions = () => {
    if (!analysisResult?.eating_order_suggestions || editedFoods.length <= 1) return null;

    const { eating_order_suggestions } = analysisResult;
    
    return (
      <Card sx={{ mb: 3, border: '1px solid #2196f3', borderRadius: '12px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#2196f3', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            🍽️ 建議進食順序
          </Typography>
          
          <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: '8px' }}>
            {Array.isArray(eating_order_suggestions) && eating_order_suggestions.length > 0 ? eating_order_suggestions.map((item, index) => {
              // 處理字串陣列的情況 ["先吃蔬菜", "再吃肉"]
              const isString = typeof item === 'string';
              
              // 終極容錯：如果物件只有一個未知的 key，直接抓它的 value
              let fallbackName = '未知食物';
              let fallbackReason = '';
              if (!isString && item && typeof item === 'object') {
                const keys = Object.keys(item);
                if (keys.length > 0 && !item.food_name && !item.name && !item.food) {
                  // AI 可能傳了 {"烤雞腿肉": "因為提供蛋白質"} 這種奇怪格式
                  fallbackName = keys[0];
                  fallbackReason = typeof item[keys[0]] === 'string' ? item[keys[0]] : '';
                }
              }

              const foodName = isString ? item : (item.food_name || item.name || item.food || fallbackName);
              const reasonText = isString ? '' : (item.reason || item.explanation || item.suggestion || fallbackReason || '提供營養與飽足感');
              
              return (
                <Box key={index} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                  <Chip 
                    label={index + 1} 
                    size="small" 
                    color="primary" 
                    sx={{ mr: 2, mt: 0.5, minWidth: '32px' }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 0.5 }}>
                      {foodName}
                    </Typography>
                    {reasonText && (
                      <Typography variant="caption" sx={{ color: '#1565c0' }}>
                        {reasonText}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }) : eating_order_suggestions.order ? eating_order_suggestions.order.map((item, index) => {
              const isString = typeof item === 'string';
              
              let fallbackName = '未知食物';
              let fallbackReason = '';
              if (!isString && item && typeof item === 'object') {
                const keys = Object.keys(item);
                if (keys.length > 0 && !item.food_name && !item.name && !item.food) {
                  fallbackName = keys[0];
                  fallbackReason = typeof item[keys[0]] === 'string' ? item[keys[0]] : '';
                }
              }

              const foodName = isString ? item : (item.food_name || item.name || item.food || fallbackName);
              const reasonText = isString ? '' : (item.reason || item.explanation || item.suggestion || fallbackReason || '提供營養與飽足感');
              
              return (
                <Box key={index} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                  <Chip 
                    label={index + 1} 
                    size="small" 
                    color="primary" 
                    sx={{ mr: 2, mt: 0.5, minWidth: '32px' }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 0.5 }}>
                      {foodName}
                    </Typography>
                    {reasonText && (
                      <Typography variant="caption" sx={{ color: '#1565c0' }}>
                        {reasonText}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }) : (
              // 處理 AI 回傳單一物件而非陣列的極端情況，例如： { "1": "吃菜", "2": "吃肉" }
              Object.entries(eating_order_suggestions).filter(([k, v]) => k !== 'explanation' && typeof v === 'string').map(([key, value], index) => (
                <Box key={index} sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
                  <Chip 
                    label={index + 1} 
                    size="small" 
                    color="primary" 
                    sx={{ mr: 2, mt: 0.5, minWidth: '32px' }}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 0.5 }}>
                      {value}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
            
            {!Array.isArray(eating_order_suggestions) && eating_order_suggestions.explanation && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #bbdefb' }}>
                <Typography variant="body2" sx={{ color: '#1976d2', fontStyle: 'italic' }}>
                  💡 {eating_order_suggestions.explanation}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // 渲染整體分析
  const renderOverallAnalysis = () => {
    if (!analysisResult?.overall_analysis) return null;

    const { overall_analysis } = analysisResult;
    
    return (
      <Card sx={{ mb: 3, border: '1px solid #607d8b', borderRadius: '12px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#607d8b', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            📊 整體營養分析
          </Typography>
          
          <Box sx={{ p: 2, backgroundColor: '#eceff1', borderRadius: '8px' }}>
            <Typography variant="body2" sx={{ color: '#455a64', lineHeight: 1.6 }}>
              {overall_analysis.summary || '這是一份營養均衡的餐點，建議適量食用並搭配運動。'}
            </Typography>
            
            {overall_analysis.recommendations && overall_analysis.recommendations.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#546e7a', mb: 1 }}>
                  營養師建議：
                </Typography>
                {overall_analysis.recommendations.map((rec, index) => (
                  <Typography key={index} variant="body2" sx={{ color: '#455a64', mb: 0.5, pl: 1 }}>
                    • {rec}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // 資料完整性驗證函數
  const validateFoodData = (food) => {
    const errors = [];
    
    if (!food.name || food.name.trim() === '') {
      errors.push('食物名稱不能為空');
    }
    
    if (!food.quantity || food.quantity <= 0) {
      errors.push('食物數量必須大於0');
    }
    
    if (!food.unit || food.unit.trim() === '') {
      errors.push('食物單位不能為空');
    }
    
    if (food.calories < 0 || food.protein < 0 || food.carbs < 0 || food.fat < 0) {
      errors.push('營養數據不能為負數');
    }
    
    if (food.calories > 10000 || food.protein > 1000 || food.carbs > 1000 || food.fat > 1000) {
      errors.push('營養數據超出合理範圍');
    }
    
    return errors;
  };

  // 檢查重複項目
  const checkDuplicateItems = (foods) => {
    const duplicates = [];
    const seen = new Set();
    
    foods.forEach((food, index) => {
      const key = `${food.name}-${food.meal_type}`;
      if (seen.has(key)) {
        duplicates.push({ index, name: food.name, meal_type: food.meal_type });
      } else {
        seen.add(key);
      }
    });
    
    return duplicates;
  };

  // 保存食物記錄 - 優化版本，支援部分成功保存
  const saveFoodRecord = async () => {
    console.log('🍽️ saveFoodRecord: 開始保存食物記錄');
    console.log('🍽️ saveFoodRecord: 要保存的食物數量:', editedFoods.length);
    console.log('🍽️ saveFoodRecord: 食物詳細資料:', editedFoods);
    
    try {
      setLoading(true);
      setIsSaving(true);
      setError(null);
      setSaveProgress(0);
      setSaveResults({ success: [], failed: [] });

      // 檢查是否有食物需要保存
      if (!editedFoods || editedFoods.length === 0) {
        console.warn('⚠️ saveFoodRecord: 沒有食物需要保存');
        setError('沒有食物需要保存');
        return;
      }

      // 資料完整性驗證
      const validationErrors = [];
      editedFoods.forEach((food, index) => {
        const errors = validateFoodData(food);
        if (errors.length > 0) {
          validationErrors.push(`食物 ${index + 1} (${food.name}): ${errors.join(', ')}`);
        }
      });

      if (validationErrors.length > 0) {
        setError(`資料驗證失敗:\n${validationErrors.join('\n')}`);
        return;
      }

      // 檢查重複項目
      const duplicates = checkDuplicateItems(editedFoods);
      if (duplicates.length > 0) {
        const duplicateMessages = duplicates.map(d => `${d.name} (${d.meal_type})`);
        console.warn('⚠️ 發現重複項目:', duplicateMessages);
        // 這裡可以選擇警告用戶但繼續保存，或者阻止保存
      }

      const totalFoods = editedFoods.length;
      const successResults = [];
      const failedResults = [];

      // 逐個保存食物記錄，支援部分成功
      for (let index = 0; index < editedFoods.length; index++) {
        const food = editedFoods[index];
        setCurrentSavingFood(food.name);
        
        console.log(`🍽️ saveFoodRecord: 處理第 ${index + 1} 個食物:`, food.name);
        
        const completionPercentage = (food.completionPercentage || globalCompletionPercentage) / 100;
        const foodRecord = {
          food_name: food.name,
          quantity: food.quantity,
          unit: food.unit,
          calories: Math.round(food.calories * food.quantity * completionPercentage),
          protein: parseFloat((food.protein * food.quantity * completionPercentage).toFixed(1)),
          carbs: parseFloat((food.carbs * food.quantity * completionPercentage).toFixed(1)),
          fat: parseFloat((food.fat * food.quantity * completionPercentage).toFixed(1)),
          meal_type: food.meal_type,
          recorded_at: new Date().toISOString(),
          completion_percentage: food.completionPercentage || globalCompletionPercentage
        };

        console.log(`🍽️ saveFoodRecord: 準備保存的食物記錄:`, foodRecord);
        
        try {
          // 加入超時機制，防止卡死
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('儲存超時，請檢查裝置儲存空間')), 10000);
          });
          
          const savePromise = calorieAIService.addFoodRecord(foodRecord);
          const result = await Promise.race([savePromise, timeoutPromise]);
          
          console.log(`✅ saveFoodRecord: 成功保存食物 ${food.name}:`, result);
          successResults.push({ food: food.name, result });
        } catch (foodError) {
          console.error(`❌ saveFoodRecord: 保存食物 ${food.name} 失敗:`, foodError);
          failedResults.push({ 
            food: food.name, 
            error: foodError.message || foodError.toString() 
          });
        }

        // 更新進度
        const progress = ((index + 1) / totalFoods) * 100;
        setSaveProgress(progress);
      }

      // 更新保存結果
      setSaveResults({ success: successResults, failed: failedResults });

      console.log('🍽️ saveFoodRecord: 保存完成');
      console.log(`✅ 成功保存: ${successResults.length} 個食物`);
      console.log(`❌ 保存失敗: ${failedResults.length} 個食物`);

      // 根據結果設置適當的消息
      if (failedResults.length === 0) {
        // 全部成功
        console.log('🍽️ saveFoodRecord: 調用 onFoodAdded 回調');
        onFoodAdded();
      } else if (successResults.length > 0) {
        // 部分成功
        const failedNames = failedResults.map(f => f.food).join(', ');
        setError(`部分保存成功！成功保存 ${successResults.length} 個食物，失敗 ${failedResults.length} 個食物 (${failedNames})。您可以重試失敗的項目。`);
        
        // 如果有成功的項目，仍然調用回調以更新UI
        if (successResults.length > 0) {
          onFoodAdded();
        }
      } else {
        // 全部失敗
        const errorMessages = failedResults.map(f => `${f.food}: ${f.error}`).join('\n');
        setError(`所有食物保存失敗:\n${errorMessages}`);
      }
      
    } catch (error) {
      console.error('❌ saveFoodRecord: 保存食物記錄失敗:', error);
      console.error('❌ saveFoodRecord: 錯誤堆疊:', error.stack);
      
      // 提供更詳細的錯誤信息
      let errorMessage = '保存失敗，請重試';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.toString) {
        errorMessage = error.toString();
      }
      
      setError(errorMessage);
    } finally {
      console.log('🍽️ saveFoodRecord: 保存流程結束');
      setLoading(false);
      setIsSaving(false);
      setCurrentSavingFood('');
      setSaveProgress(0);
    }
  };

  // 重新拍照
  const retakePhoto = () => {
    setCapturedImage(null);
    setAnalysisResult(null);
    setEditedFoods([]);
    setEditMode({});
    setStep('capture');
    setError(null);
  };

  const renderCaptureStep = () => (
    <Box sx={{ 
      p: 2, 
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
        拍攝食物照片 📸
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
          請拍攝或選擇食物照片，AI 將自動識別並計算營養成分
        </Typography>
      </Box>

      {/* AI 模型選擇器 */}
      <Card sx={{ mb: 3, borderRadius: '12px', backgroundColor: '#f8f9fa' }}>
        <CardContent sx={{ py: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#333' }}>
            🤖 選擇 AI 模型
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              }
            }}
          >
            {aiService.getAvailableModels().map((model) => (
              <MenuItem key={model} value={model}>
                {aiService.getModelDisplayName(model)}
              </MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            不同模型可能有不同的識別準確度和速度
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<CameraIcon />}
            onClick={takePicture}
            disabled={loading}
            sx={{
              py: 2,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
              fontSize: '1.1rem',
              '&:hover': {
                background: 'linear-gradient(135deg, #FF5252, #FF7979)',
              }
            }}
          >
            使用相機拍照
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIcon={<PhotoLibraryIcon />}
            onClick={selectFromGallery}
            disabled={loading}
            sx={{
              py: 2,
              borderRadius: '16px',
              borderColor: '#FF6B6B',
              color: '#FF6B6B',
              fontSize: '1.1rem',
              '&:hover': {
                borderColor: '#FF5252',
                backgroundColor: 'rgba(255, 107, 107, 0.04)',
              }
            }}
          >
            從相簿選擇
          </Button>
        </Grid>
      </Grid>

      {/* 備用檔案輸入 */}
      <input
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        ref={fileInputRef}
        style={{ display: 'none' }}
      />
      


      {loading && (
        <Box sx={{ mt: 3 }}>
          <CircularProgress sx={{ color: '#FF6B6B' }} />
          <Typography variant="body2" sx={{ mt: 1 }}>
            正在處理照片...
          </Typography>
        </Box>
      )}

      {/* 無食物檢測提示 */}
      {error === 'no_food_detected' && (
        <Alert 
          severity="warning" 
          sx={{ 
            mt: 2, 
            borderRadius: '12px',
            backgroundColor: '#FFF3E0',
            border: '1px solid #FFB74D'
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            🔍 未檢測到食物
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            AI 無法在此圖片中識別出任何食物。請嘗試：
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              確保食物清晰可見且光線充足
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              將相機靠近食物拍攝
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              避免背景過於複雜
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setError(null)}
              sx={{
                backgroundColor: '#FF9800',
                '&:hover': { backgroundColor: '#F57C00' }
              }}
            >
              重新拍照
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                // 提供手動添加選項
                const manualFood = {
                  foods: [{
                    name: '手動輸入',
                    calories: 100,
                    protein: 0,
                    carbs: 0,
                    fat: 0,
                    quantity: 1,
                    unit: '份',
                    confidence: 1.0,
                    healthyIndex: 3
                  }],
                  confidence: 1.0,
                  notes: '請編輯食物資訊'
                };
                setAnalysisResult(manualFood);
                const initialEditedFoods = manualFood.foods.map(food => ({
                  ...food,
                  meal_type: selectedMealType,
                  completionPercentage: globalCompletionPercentage
                }));
                setEditedFoods(initialEditedFoods);
                setEditMode({ 0: true });
                setError(null);
                setStep('confirm');
              }}
              sx={{
                borderColor: '#FF9800',
                color: '#FF9800',
                '&:hover': { 
                  borderColor: '#F57C00',
                  backgroundColor: 'rgba(255, 152, 0, 0.04)'
                }
              }}
            >
              手動輸入
            </Button>
          </Box>
        </Alert>
      )}
    </Box>
  );

  const renderAnalyzeStep = () => (
    <Box sx={{ 
      p: 1, 
      textAlign: 'center',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
        AI 分析中 🤖
      </Typography>

      {capturedImage && (
        <Card sx={{ mb: 2, borderRadius: '16px', overflow: 'hidden', p: 0 }}>
          <img
            src={capturedImage}
            alt="拍攝的食物"
            style={{
              width: '100%',
              height: '300px', // Increased from 150px
              objectFit: 'contain', // Changed from cover to contain to show full image
              backgroundColor: '#f5f5f5', // Add background color for empty spaces
              borderRadius: '16px',
              cursor: 'pointer',
              display: 'block'
            }}
            onClick={() => setImageModalOpen(true)}
          />
        </Card>
      )}

      <Box sx={{ mb: 2 }}>
        <CircularProgress size={50} sx={{ color: '#FF6B6B', mb: 1 }} />
        <Typography variant="body1" color="textSecondary">
          AI 正在識別食物並分析營養成分...
        </Typography>
      </Box>

      <Button
        variant="outlined"
        onClick={retakePhoto}
        sx={{
          borderColor: '#FF6B6B',
          color: '#FF6B6B',
          '&:hover': {
            borderColor: '#FF5252',
            backgroundColor: 'rgba(255, 107, 107, 0.04)',
          }
        }}
      >
        重新拍照
      </Button>
    </Box>
  );

  // 處理單個食物的編輯
  const handleEditFood = (index, field, value) => {
    const updatedFoods = [...editedFoods];
    updatedFoods[index] = { ...updatedFoods[index], [field]: value };
    setEditedFoods(updatedFoods);
  };

  // 處理全局餐型選擇
  const handleMealTypeChange = (mealType) => {
    setSelectedMealType(mealType);
    // 更新所有食物的餐型
    const updatedFoods = editedFoods.map(food => ({
      ...food,
      meal_type: mealType
    }));
    setEditedFoods(updatedFoods);
  };

  // 處理全局食用比例變更
  const handleGlobalCompletionPercentageChange = (value) => {
    setGlobalCompletionPercentage(value);
    // 更新所有食物的食用比例
    const updatedFoods = editedFoods.map(food => ({
      ...food,
      completionPercentage: value
    }));
    setEditedFoods(updatedFoods);
  };

  // 切換編輯模式
  const toggleEditMode = (index) => {
    setEditMode(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // 刪除食物項目
  const deleteFoodItem = (index) => {
    const updatedFoods = editedFoods.filter((_, i) => i !== index);
    setEditedFoods(updatedFoods);
    
    // 更新編輯模式狀態
    const updatedEditMode = {};
    updatedFoods.forEach((_, i) => {
      updatedEditMode[i] = editMode[i] || false;
    });
    setEditMode(updatedEditMode);
  };

  const renderConfirmStep = () => {
    // 計算營養總計 - 考慮完成度百分比
    const totalNutrition = editedFoods.reduce((total, food) => {
      const quantity = food.quantity || 1;
      const completionPercentage = (food.completionPercentage || globalCompletionPercentage) / 100;
      return {
        calories: total.calories + ((food.calories || 0) * quantity * completionPercentage),
        protein: total.protein + ((food.protein || 0) * quantity * completionPercentage),
        carbs: total.carbs + ((food.carbs || 0) * quantity * completionPercentage),
        fat: total.fat + ((food.fat || 0) * quantity * completionPercentage)
      };
    }, { calories: 0, protein: 0, carbs: 0, fat: 0 });

    return (
      <Box sx={{ 
        p: 2,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: '#333' }}>
          確認食物資訊 ✅
        </Typography>

        {capturedImage && (
          <Card sx={{ mb: 2, borderRadius: '16px', overflow: 'hidden', p: 0 }}>
            <img
              src={capturedImage}
              alt="識別的食物"
              style={{
                width: '100%',
                height: '300px', // Increased from 180px
                objectFit: 'contain', // Changed from cover to contain to show full image
                backgroundColor: '#f5f5f5', // Add background color for empty spaces
                borderRadius: '16px',
                cursor: 'pointer',
                display: 'block'
              }}
              onClick={() => setImageModalOpen(true)}
            />
          </Card>
        )}

        {/* 顯示識別到的食物數量 */}
        {editedFoods.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              AI 識別結果
            </Typography>
            <Chip 
              label={`${editedFoods.length} 項食物`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
            {analysisResult && (
              <Typography variant="caption" color="textSecondary">
                信心度: {Math.round(analysisResult.confidence * 100)}%
              </Typography>
            )}
          </Box>
        )}

        {/* 餐型選擇區域 */}
        {editedFoods.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
              餐型
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {mealTypes.map((mealType) => (
                <Chip
                  key={mealType.value}
                  label={mealType.label}
                  onClick={() => handleMealTypeChange(mealType.value)}
                  color={selectedMealType === mealType.value ? 'primary' : 'default'}
                  variant={selectedMealType === mealType.value ? 'filled' : 'outlined'}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: selectedMealType === mealType.value 
                        ? 'primary.dark' 
                        : 'action.hover'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* 全局食用比例控制 */}
        <Card sx={{ 
          mb: 3, 
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #FFF3E0, #E8F5E8)',
          border: '2px solid #FF9800'
        }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ 
              mb: 1, 
              fontWeight: 'bold',
              color: '#FF9800',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              🍽️ 實際食用比例
            </Typography>
            
            <Box sx={{ px: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                實際食用份量：{globalCompletionPercentage}%
              </Typography>
              
              <Slider
                value={globalCompletionPercentage}
                onChange={(e, value) => handleGlobalCompletionPercentageChange(value)}
                min={0}
                max={100}
                step={5}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 25, label: '25%' },
                  { value: 50, label: '50%' },
                  { value: 75, label: '75%' },
                  { value: 100, label: '100%' }
                ]}
                sx={{
                  color: '#FF9800',
                  '& .MuiSlider-thumb': {
                    backgroundColor: '#FF9800',
                    '&:hover': {
                      boxShadow: '0px 0px 0px 8px rgba(255, 152, 0, 0.16)'
                    }
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#FF9800'
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: '#FFE0B2'
                  },
                  '& .MuiSlider-mark': {
                    backgroundColor: '#FF9800'
                  },
                  '& .MuiSlider-markLabel': {
                    color: '#FF9800',
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 營養總計卡片 - 只在多個食物時顯示 */}
        {editedFoods.length > 1 && (
          <Card sx={{ 
            mb: 3, 
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #E3F2FD, #F3E5F5)',
            border: '2px solid #2196F3'
          }}>
            <CardContent>
              <Typography variant="h6" sx={{ 
                fontWeight: 'bold', 
                color: '#1976D2', 
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                📊 營養總計
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#FF6B6B', fontWeight: 'bold' }}>
                      {Math.round(totalNutrition.calories)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      卡路里
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                      {parseFloat(totalNutrition.protein.toFixed(1))}g
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      蛋白質
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#2196F3', fontWeight: 'bold' }}>
                      {parseFloat(totalNutrition.carbs.toFixed(1))}g
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      碳水
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#FFC107', fontWeight: 'bold' }}>
                      {parseFloat(totalNutrition.fat.toFixed(1))}g
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      脂肪
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* 顯示每個食物項目 */}
        {editedFoods.map((food, index) => (
          <Card key={index} sx={{ mb: 2, borderRadius: '16px' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {food.name}
                </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => toggleEditMode(index)}
                  sx={{ color: '#FF6B6B' }}
                >
                  <EditIcon />
                </IconButton>
                {editedFoods.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => deleteFoodItem(index)}
                    sx={{ color: '#f44336' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Box>

            {editMode[index] ? (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="食物名稱"
                    value={food.name || ''}
                    onChange={(e) => handleEditFood(index, 'name', e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="數量"
                    type="number"
                    value={food.quantity || 1}
                    onChange={(e) => handleEditFood(index, 'quantity', e.target.value === '' ? '' : parseFloat(e.target.value))}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="單位"
                    value={food.unit || ''}
                    onChange={(e) => handleEditFood(index, 'unit', e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                    營養成分 (每份)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="卡路里"
                        type="number"
                        value={food.calories || 0}
                        onChange={(e) => handleEditFood(index, 'calories', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="蛋白質(g)"
                        type="number"
                        value={food.protein || 0}
                        onChange={(e) => handleEditFood(index, 'protein', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="碳水(g)"
                        type="number"
                        value={food.carbs || 0}
                        onChange={(e) => handleEditFood(index, 'carbs', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="脂肪(g)"
                        type="number"
                        value={food.fat || 0}
                        onChange={(e) => handleEditFood(index, 'fat', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{food.quantity} {food.unit}</span>
                  <span>信心度: {Math.round((food.confidence || 0.5) * 100)}%</span>
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: '#FF6B6B', fontWeight: 'bold' }}>
                        {Math.round((food.calories || 0) * (food.quantity || 1) * (food.completionPercentage || globalCompletionPercentage) / 100)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                        {Math.round((food.calories || 0) * (food.quantity || 1))} 卡的 {food.completionPercentage || globalCompletionPercentage}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                        卡路里
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>
                        {parseFloat((((food.protein || 0) * (food.quantity || 1)) * (food.completionPercentage || globalCompletionPercentage) / 100).toFixed(1))}g
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        蛋白質
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>
                        {parseFloat((((food.carbs || 0) * (food.quantity || 1)) * (food.completionPercentage || globalCompletionPercentage) / 100).toFixed(1))}g
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        碳水
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 'normal' }}>
                        {parseFloat((((food.fat || 0) * (food.quantity || 1)) * (food.completionPercentage || globalCompletionPercentage) / 100).toFixed(1))}g
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        脂肪
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
            

            
            {/* 健康指數進度條 */}
            <Box sx={{ mt: 2, px: 2, pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  健康指數
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {food.healthyIndex || 3}/5
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={((food.healthyIndex || 3) / 5) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#f0f0f0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: 
                      (food.healthyIndex || 3) >= 4 ? '#4CAF50' : // 綠色 - 健康 (4-5)
                      (food.healthyIndex || 3) >= 3 ? '#FFC107' : // 黃色 - 中等 (3)
                      '#F44336' // 紅色 - 不健康 (1-2)
                  }
                }}
              />
            </Box>

            {/* 新增：升糖指數 (GI) & 替代方案 */}
            {(food.gi_level || food.healthier_alternative) && (
              <Box sx={{ mt: 1, px: 2, pb: 2 }}>
                {food.gi_level && (
                  <Typography variant="body2" sx={{ mb: 0.5, color: '#e67e22' }}>
                    <strong>📊 升糖指數預估:</strong> {food.gi_level}
                  </Typography>
                )}
                {food.healthier_alternative && (
                  <Typography variant="body2" sx={{ color: '#27ae60' }}>
                    <strong>💡 更好的選擇:</strong> {food.healthier_alternative}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      ))}

      {/* 專業營養師分析 - 整合所有 AI 分析內容 */}
      <Card sx={{ mb: 3, border: '1px solid #e9ecef' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 'bold' }}>
            🥗 專業營養師分析
          </Typography>
          
          {/* 基本營養分析 */}
          <Box sx={{ pl: 2, mb: 3 }}>
            {generateNutritionalAnalysis(editedFoods)}
          </Box>

          {/* AI 詳細分析內容 */}
          {analysisResult && (
            <>
              {/* 血糖分析 */}
              {renderGlycemicAnalysis()}

              {/* 消化分析 */}
              {renderDigestionAnalysis()}

              {/* 個人化建議 */}
              {renderPersonalizedAdvice()}

              {/* 進食順序建議 */}
              {renderEatingOrderSuggestions()}

              {/* 整體分析 */}
              {renderOverallAnalysis()}
            </>
          )}

          {/* AI 識別說明 */}
          {analysisResult?.notes && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1 }}>
                🤖 識別說明
              </Typography>
              <Typography variant="body2" sx={{ color: '#495057' }}>
                {analysisResult.notes}
              </Typography>
            </Box>
          )}

          {/* 新增：具體健康建議 (Health Tips) */}
          {analysisResult?.health_tips && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
              <Typography variant="subtitle2" sx={{ color: '#2e7d32', mb: 1 }}>
                💚 健康小叮嚀
              </Typography>
              <Typography variant="body2" sx={{ color: '#1b5e20' }}>
                {analysisResult.health_tips}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

        {/* 保存進度指示器 */}
        {isSaving && (
          <Card sx={{ mb: 2, backgroundColor: '#e3f2fd', border: '1px solid #2196f3' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CircularProgress size={20} sx={{ mr: 1, color: '#2196f3' }} />
                <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                  正在保存食物記錄...
                </Typography>
              </Box>
              
              {currentSavingFood && (
                <Typography variant="body2" sx={{ color: '#1976d2', mb: 1 }}>
                  當前保存: {currentSavingFood}
                </Typography>
              )}
              
              <LinearProgress
                variant="determinate"
                value={saveProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#bbdefb',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    backgroundColor: '#2196f3'
                  }
                }}
              />
              
              <Typography variant="caption" sx={{ color: '#1976d2', mt: 0.5, display: 'block' }}>
                進度: {Math.round(saveProgress)}%
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* 保存結果摘要 */}
        {saveResults.success.length > 0 || saveResults.failed.length > 0 ? (
          <Card sx={{ mb: 2, backgroundColor: '#f3e5f5', border: '1px solid #9c27b0' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="body2" sx={{ color: '#7b1fa2', fontWeight: 'bold', mb: 1 }}>
                保存結果摘要
              </Typography>
              
              {saveResults.success.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#388e3c' }}>
                    ✅ 成功保存 {saveResults.success.length} 個食物:
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#4caf50', ml: 2 }}>
                    {saveResults.success.map(s => s.food).join(', ')}
                  </Typography>
                </Box>
              )}
              
              {saveResults.failed.length > 0 && (
                <Box>
                  <Typography variant="body2" sx={{ color: '#d32f2f' }}>
                    ❌ 保存失敗 {saveResults.failed.length} 個食物:
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#f44336', ml: 2 }}>
                    {saveResults.failed.map(f => f.food).join(', ')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ) : null}

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              onClick={retakePhoto}
              disabled={loading || isSaving}
              sx={{
                py: 2,
                borderColor: '#FF6B6B',
                color: '#FF6B6B',
                '&:hover': {
                  borderColor: '#FF5252',
                  backgroundColor: 'rgba(255, 107, 107, 0.04)',
                }
              }}
            >
              重新拍照
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="contained"
              onClick={saveFoodRecord}
              disabled={loading || isSaving}
              sx={{
                py: 2,
                background: isSaving 
                  ? 'linear-gradient(135deg, #9e9e9e, #bdbdbd)' 
                  : 'linear-gradient(135deg, #4CAF50, #66BB6A)',
                '&:hover': {
                  background: isSaving 
                    ? 'linear-gradient(135deg, #9e9e9e, #bdbdbd)' 
                    : 'linear-gradient(135deg, #43A047, #5CB85C)',
                }
              }}
            >
              {isSaving ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  保存中...
                </Box>
              ) : loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                '確認添加'
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* 頂部導航 */}
      <Paper sx={{ 
        p: 1.5, 
        display: 'flex', 
        alignItems: 'center',
        backgroundColor: 'white',
        borderRadius: 0,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        flexShrink: 0
      }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          食物識別
        </Typography>
      </Paper>

      {/* 錯誤提示 */}
      {error && (
        <Alert severity="error" sx={{ m: 1, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      {/* 主要內容 */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}>
        {step === 'capture' && renderCaptureStep()}
        {step === 'analyze' && renderAnalyzeStep()}
        {step === 'confirm' && renderConfirmStep()}
      </Box>

      {/* 圖片彈出視窗 */}
      <Modal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={imageModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90vw',
              height: '90vh',
              bgcolor: 'background.paper',
              borderRadius: '16px',
              boxShadow: 24,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {/* 關閉按鈕 */}
            <IconButton
              onClick={() => setImageModalOpen(false)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                bgcolor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* 完整大小的圖片 */}
            {capturedImage && (
              <img
                src={capturedImage}
                alt="食物完整圖片"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: '8px'
                }}
              />
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default FoodIdentification;